import axios from "axios";

class BorrowerSummaryService {
  constructor() {
    this.apiEndpoint =
      process.env.REACT_APP_AI_API_ENDPOINT ||
      "https://api.openai.com/v1/chat/completions";
    this.apiKey = process.env.REACT_APP_AI_API_KEY;
    this.model = process.env.REACT_APP_AI_MODEL || "gpt-4o-mini";
  }

  async generateBorrowerSummary(borrower) {
    if (!borrower) {
      throw new Error("Missing borrower profile");
    }

    if (!this.apiKey) {
      throw new Error("AI API key not configured");
    }

    const profile = this.buildProfile(borrower);
    const prompt = this.buildPrompt(profile);

    const response = await axios.post(
      this.apiEndpoint,
      {
        model: this.model,
        messages: [
          {
            role: "system",
            content:
              "You are a professional credit analyst who writes concise, executive borrower summaries for internal dashboards.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.2,
        max_tokens: 350,
      },
      {
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          "Content-Type": "application/json",
        },
      }
    );

    const summary = response?.data?.choices?.[0]?.message?.content?.trim();

    if (!summary) {
      throw new Error("AI summary response was empty");
    }

    return this.cleanSummary(summary);
  }

  getFallbackSummary(borrower) {
    const profile = this.buildProfile(borrower || {});
    return this.buildFallbackSummary(profile);
  }

  buildProfile(borrower) {
    const loans = Array.isArray(borrower?.loans) ? borrower.loans : [];
    const normalize = (value) => {
      if (value === null || value === undefined) return 0;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const totalBorrowed = loans.reduce(
      (sum, loan) => sum + normalize(loan.amount),
      0
    );
    const totalPaid = loans.reduce(
      (sum, loan) => sum + normalize(loan.paid_amount),
      0
    );
    const outstandingBalance = Math.max(totalBorrowed - totalPaid, 0);
    const paymentRatio =
      totalBorrowed > 0 ? (totalPaid / totalBorrowed) * 100 : 0;

    const countByStatus = (statuses) =>
      loans.filter((loan) => statuses.includes(loan.loan_status)).length;

    const mostRecentLoanDate = loans
      .map(
        (loan) =>
          loan.updated_at ||
          loan.date_created ||
          loan.created_at ||
          loan.disbursed_at
      )
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0];

    const loanTypes = Array.from(
      new Set(loans.map((loan) => loan.loan_type).filter(Boolean))
    );

    return {
      name:
        `${borrower?.first_name || ""} ${borrower?.last_name || ""}`.trim() ||
        "Borrower",
      creditScore: normalize(borrower?.credit_score),
      employer: borrower?.employer || borrower?.company || null,
      income: normalize(borrower?.salary || borrower?.monthly_income),
      riskRating: borrower?.risk_rating || null,
      loanHistoryCount: loans.length,
      totalBorrowed,
      totalPaid,
      outstandingBalance,
      paymentRatio,
      activeLoans: countByStatus([7]),
      overdueLoans: countByStatus([8, 9]),
      completedLoans: countByStatus([10]),
      loanTypes,
      avgLoanAmount: loans.length ? totalBorrowed / loans.length : 0,
      delinquencyRate: loans.length
        ? (countByStatus([8, 9]) / loans.length) * 100
        : 0,
      lastLoanDate: mostRecentLoanDate
        ? new Date(mostRecentLoanDate).toISOString().split("T")[0]
        : null,
    };
  }

  buildPrompt(profile) {
    const formattedProfile = {
      ...profile,
      paymentRatio: Number(profile.paymentRatio || 0).toFixed(1),
      avgLoanAmount: Number(profile.avgLoanAmount || 0).toFixed(2),
      delinquencyRate: Number(profile.delinquencyRate || 0).toFixed(1),
    };

    return `Create a single paragraph (maximum 90 words) professional borrower summary using this dataset:
${JSON.stringify(formattedProfile, null, 2)}

Instructions:
- Keep the tone factual and analytical for an internal lending dashboard.
- Mention repayment behaviour (payment ratio, delinquencies), current exposure, loan mix, and credit score when available.
- Highlight key risks or reassuring factors.
- Conclude with a recommended monitoring posture such as "monitor closely" or "maintain normal follow-up".`;
  }

  cleanSummary(summary) {
    return summary
      .replace(/^["'\s]+|["'\s]+$/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  buildFallbackSummary(profile) {
    const parts = [];
    parts.push(
      `${profile.name} has ${profile.loanHistoryCount || 0} recorded loan${profile.loanHistoryCount === 1 ? "" : "s"}.`
    );

    if (profile.creditScore) {
      parts.push(`Credit score stands at ${profile.creditScore}.`);
    }

    if (profile.paymentRatio) {
      parts.push(`Repayment ratio is ${profile.paymentRatio.toFixed(1)}%.`);
    }

    parts.push(
      `Outstanding exposure totals ZMW ${this.formatCurrency(profile.outstandingBalance || 0)} ` +
        `across ${profile.activeLoans || 0} active loan${profile.activeLoans === 1 ? "" : "ies"}.`
    );

    if (profile.overdueLoans) {
      parts.push(
        `${profile.overdueLoans} facility${profile.overdueLoans === 1 ? " is" : "ies are"} overdue and need attention.`
      );
    } else {
      parts.push("No delinquent facilities are currently recorded.");
    }

    parts.push("Maintain close monitoring ahead of the next repayment cycle.");

    return parts.join(" ");
  }

  formatCurrency(amount = 0) {
    return new Intl.NumberFormat("en-ZM", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}

export default BorrowerSummaryService;
